import { mapValues, get, groupBy, assign, cloneDeep } from "lodash-es";
import { v4 as uuidv4 } from "uuid";
import { splitUid, toUid } from "@/helpers/utility/Id";

import {
  acceptHotItemStore,
  defineItemStore
} from "@editor/items/itemStore.store";

import { formatDate } from "@/i18n";
import { formatNumber } from "@/helpers/utility/Formatting";
import { i18n } from "@/i18n";

import Simulation from "@editor/items/simulation/Simulation";

import { useSettingsStore } from "@editor/store/settings.store";
import { useModelStore } from "@editor/items/model/model.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useEffectStore } from "@editor/items/effect/effects.store";
import { useActionStore } from "@editor/items/action/actions.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useIncidentStore } from "@editor/items/incident/incidents.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useScenarioStore } from "@editor/items/scenario/scenarios.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";

import { downloadBlob } from "@/helpers/data/Download";
import { formatRoundName } from "@editor/items/simulationRound/useRoundTime";
import { calculateExpressionValues } from "@editor/items/index/indexFunctions";

import type { SimulationItem } from "./SimulationItem";
import type { ActionItem } from "../action/ActionItem";
import type { IncidentItem } from "../incident/IncidentItem";
import type { EffectItem } from "@editor/items/effect/EffectItem";
import type {
  PendingSimulationRoundEffect,
  SimulationRoundItem
} from "../simulationRound/SimulationRoundItem";
import type { ResourceItem } from "../resource/ResourceItem";
import type { TargetItem } from "../target/TargetItem";
import type { ImpulseItem } from "../impulse/ImpulseItem";
import type { ActionPlan } from "../simulationRound/ActionPlan";
import type { ExpressionValue } from "../expression/expressionFunctions";
import type { ScheduledEffectItem } from "../scheduledEffect/ScheduledEffectItem";

export const simulationItemStructure: SimulationItem = {
  type: "simulation",
  id: 0,
  uid: "simulation_0",
  createdAt: -1,
  docs: {},
  order: 0,

  title: "",
  description: "",

  complete: false,
  currentRoundIndex: -1, // -1 while the simulation did not start
  maxRounds: -1,
  startElementValues: {},

  scenarioId: null,
  actionIds: [],
  incidentIds: [],
  targetIds: [],
  resourceIds: [],
  indexIds: [],

  useMomentums: true,
  useScheduledEffects: false,

  endReason: null,

  roundUnit: "round",

  /**
   * The actionplan is the place where all the actions for the next rounds are placed.
   */
  actionPlans: {},

  rounds: [],

  roundLength: 1,

  timeStart: null,

  durationType: "roundAmount",

  // not used right now, maybe in future
  randomSeed: Math.floor(Math.random() * 10000)
};

export const useSimulationStore = defineItemStore(
  "model/simulations",
  simulationItemStructure,
  {
    getters: {
      titles(state): { [id: string]: string } {
        return mapValues(state.items, "title");
      },
      byScenario(state): { [scenarioId: string]: SimulationItem[] } {
        return groupBy(state.items, "scenarioId");
      },
      /**
       * The last rounds of all the simulations.
       * Contains the last completed round of each simulation if available,
       * otherwise returns the start state round.
       */
      lastRounds(state): { [simulationId: string]: SimulationRoundItem } {
        return mapValues(state.items, (simulation) => {
          const rounds = simulation.rounds || [];

          if (simulation.complete) return rounds[simulation.currentRoundIndex];

          // if no rounds were completed, return round "-1"
          if (simulation.currentRoundIndex <= 0) {
            const sim = new Simulation(simulation);
            return sim.virtualGameStartRound;
          }

          return rounds[simulation.currentRoundIndex - 1];
        });
      },

      getImpulses(): (simulationId: number) => {
        [roundIndex: number]: {
          target: {
            [targetId: number]: ImpulseItem;
          };
          resource: {
            [resourceId: number]: ImpulseItem;
          };
        };
      } {
        return (simulationId: number) => {
          const simulation = this.items[simulationId];
          return simulation.rounds.reduce(
            (
              roundList: {
                [roundIndex: number]: {
                  target: { [targetId: number]: ImpulseItem };
                  resource: { [resourceId: number]: ImpulseItem };
                };
              },
              round
            ) => {
              roundList[round.index] = round.impulses ?? {};
              return roundList;
            },
            {}
          );
        };
      },

      /**
       * Contains the current value of each simulation, based on its last round.
       * The value is calculated based on the value of each target.
       * @deprecated Use the totalPerformance index instead.
       */
      getValue(): (id: number) => number {
        /**
         * @param id - the id of the simulation
         */
        return (id: number) => {
          const lastRound = this.lastRounds[id];

          // Todo: Limit this to target
          // Todo: remove this function. We're using the totalPerformance index in future.

          if (lastRound == null) return 0;
          const eValues = Object.values(lastRound.elementValues);
          if (eValues.length === 0) return 0;
          return (
            eValues.reduce((sum, group) => sum + Number(group.value), 0) / eValues.length
          );
        };
      },

      /**
       * Contains the value of each simulation based on the simulations start values.
       * @deprecated Use the totalPerformance index instead.
       */
      getStartValue(): (id: number) => number {
        /**
         * @param id - the id of the simulation
         */
        return (id: number) => {
          const simulation = this.byId[id];

          const startElValues = Object.values(simulation.startElementValues);
          if (startElValues.length === 0) return 0;
          return (
            startElValues.reduce((sum, group) => sum + Number(group.value), 0) /
            startElValues.length
          );
        };
      },

      /**
       * @deprecated Use the totalPerformance index instead.
       * @returns The average value of all targets in the simulation.
       */
      getRoundValues(): (simulationId: number) => number[] {
        /**
         * @param simulationId - The id of the simulation to get the values for
         */
        return (simulationId: number) => {
          const simulation = this.byId[simulationId];
          const rounds = simulation?.rounds ?? [];

          return rounds.map((round) => {
            const eleValues = Object.values(round.elementValues);
            if (eleValues.length === 0) return 0;
            return (
              eleValues.reduce((sum, group) => sum + Number(group.value), 0) /
              eleValues.length
            );
          });
        };
      },

      targetIds(state): { [simulationId: string]: number[] } {
        return mapValues(state.items, (simulation) => simulation.targetIds);
      },

      getRounds(): (simulationId: number) => SimulationRoundItem[] {
        return (simulationId: number) => this.byId[simulationId].rounds;
      },

      getActionsByRound(): (simulationId: number) => {
        [roundIndex: string]: ActionItem[];
      } {
        return (simulationId: number) => {
          const simulation = this.byId[simulationId];
          const actions = useActionStore().items;

          const roundActions: { [roundIndex: string]: ActionItem[] } = {};
          for (let i = 0; i < simulation.maxRounds; i++) {
            const roundActionPlan = this.getActionPlan(simulationId, i);
            for (const actionId of roundActionPlan.plannedActionIds) {
              if (roundActions[i] == null) roundActions[i] = [];
              roundActions[i].push(actions[actionId]);
            }
          }
          return roundActions;
        };
      },

      getIncidentsByRound(): (simulationId: number) => {
        [roundIndex: string]: IncidentItem[];
      } {
        return (simulationId: number) => {
          const rounds = this.getRounds(simulationId);
          const incidents = useIncidentStore().items;

          const roundIncidents: { [roundIndex: string]: IncidentItem[] } = {};
          rounds.forEach((round, roundIndex) => {
            for (const incidentReason of round.activatedIncidents) {
              if (roundIncidents[roundIndex] == null)
                roundIncidents[roundIndex] = [];
              roundIncidents[roundIndex].push(
                incidents[incidentReason.incidentId]
              );
            }
          });
          return roundIncidents;
        };
      },

      /**
       * Get all effects that were created in a previous round.
       */
      getPendingEffectsByRound(): (simulationId: number) => {
        [roundIndex: string]: PendingSimulationRoundEffect[];
      } {
        return (simulationId: number) => {
          const pendingEffectsByRound: {
            [roundIndex: string]: PendingSimulationRoundEffect[];
          } = {};
          const effectsByType = {
            action: useEffectStore().byAction,
            incident: useEffectStore().byIncident
          };

          const simulation = this.byId[simulationId];

          const actionsByRound = this.getActionsByRound(simulationId);
          const incidentsByRound = this.getIncidentsByRound(simulationId);

          // pre-create the groups for each round
          const maxRounds = simulation?.maxRounds ?? 0;
          for (let roundIndex = 0; roundIndex < maxRounds; roundIndex++) {
            pendingEffectsByRound[roundIndex] =
              pendingEffectsByRound[roundIndex] ?? [];

            const actions = actionsByRound[roundIndex] ?? [];
            const incidents = incidentsByRound[roundIndex] ?? [];

            // go through all the actions and incidents that were activated
            // in that round.
            // We'll add all the delayed effects to the following rounds.
            for (const item of [...actions, ...incidents]) {
              if (item == null) continue;

              let effects: EffectItem[] = (
                effectsByType[item.type][item.id] ?? []
              ).slice();

              let roundOffset = 0;

              while (effects.length > 0) {
                roundOffset++;

                let pendingEffectGroup: PendingSimulationRoundEffect | null =
                  null;

                effects = effects.filter((effect) => {
                  if (effect.impulseExpressions.length <= roundOffset) {
                    return false;
                  }

                  const expression = effect.impulseExpressions[roundOffset];

                  // if the expression is zero, don't add the action/incident to
                  // the round, but keep the effect in the list, so that later effects
                  // are added.
                  if (expression === "+0" || expression === "") {
                    return true;
                  }

                  if (pendingEffectGroup == null) {
                    pendingEffectGroup = {
                      roundIndex: roundIndex,
                      roundOffset: roundOffset,
                      type: item.type,
                      id: item.id,
                      effects: []
                    };
                  }

                  pendingEffectGroup.effects.push({
                    ...effect,
                    hint: "", // clear the hint. The hint is only for the first affected round.
                    impulseExpressions: [expression]
                  });

                  return true;
                });

                if (pendingEffectGroup) {
                  const roundGroups =
                    pendingEffectsByRound[roundIndex + roundOffset] ?? [];
                  roundGroups.push(pendingEffectGroup);
                  pendingEffectsByRound[roundIndex + roundOffset] = roundGroups;
                }
              }
            }
          }

          return pendingEffectsByRound;
        };
      },

      getActionPlan(): (
        simulationId: number,
        roundIndex: string | number
      ) => ActionPlan {
        return (simulationId: number, roundIndex: string | number) => {
          const simulation = this.byId[simulationId];
          const roundActionPlan = simulation?.actionPlans?.[roundIndex];
          if (roundActionPlan) return roundActionPlan;

          return Simulation.createRoundActionPlan();
        };
      },

      getExportText(): (id: number) => string {
        return (id: number) => {
          const { t } = i18n;
          const simulation = this.byId[id];
          const actionsByRound = this.getActionsByRound(id);
          const incidentsByRound = this.getIncidentsByRound(id);
          const rounds = this.getRounds(id);

          const gameContext = {
            simulationId: id,
            rounds
          };

          let text = `${simulation.title}\n\n`;

          // === START STATE ===

          text += `${t("labels.startState").toUpperCase()}\n\n`;

          text += `\t${t("objectNames.target", 2).toUpperCase()}\n`;

          // write simulation start target values
          const targetStore = useTargetStore();
          for (const targetId of simulation.targetIds) {
            const target = targetStore.items[targetId];

            const valueText = `${
              Math.round(Number(simulation.startElementValues[`target_${targetId}`].value) * 1000) / 1000
            }`;
            text += `\t${target.title.replace("\n", "")}${t(
              ":"
            )}  ${valueText}\n`;
          }
          text += "\n";

          // RESSOURCES
          const resources = useResourceStore().items;
          const resourceCount = Object.values(resources).length;

          if (resourceCount > 0) {
            text += `\t${t("objectNames.resource", 2).toUpperCase()}\n`;

            // write simulation start target values
            for (const resourceId of simulation.resourceIds) {
              const resource = resources[resourceId];
              const uid = `resource_${resourceId}`;

              const valueText = `${
                Math.round(Number(simulation.startElementValues[uid].value) * 1000) /
                1000
              }`;
              text += `\t${resource.title.replace("\n", "")}${t(
                ":"
              )}  ${valueText}\n`;
            }

            text += "\n";
          }

          const indexStore = useIndexStore();
          const indices = indexStore.items;
          const indexCount = indexStore.length;

          if (indexCount > 0) {
            text += `\t${t("objectNames.index", 2).toUpperCase()}\n`;

            // write simulation start index values
            for (const indexId of simulation.indexIds) {
              const uid = `index_${indexId}`;
              const index = indices[indexId];

              const valueText = `${
                Math.round(Number(simulation.startElementValues[uid].value) * 1000) / 1000
              }`;
              text += `\t${index.title.replace("\n", "")}${t(
                ":"
              )}  ${valueText}\n`;
            }

            text += "\n";
          }

          // === END STATE ===

          const lastRound = this.lastRounds[id];

          const endRoundName = formatRoundName(
            simulation.durationType,
            simulation.roundUnit,
            simulation.roundLength,
            lastRound.index,
            simulation.timeStart
          );

          text += `${i18n
            .t("labels.endState")
            .toUpperCase()} (${endRoundName})\n\n`;

          text += `\t${t("objectNames.target", 2).toUpperCase()}\n`;

          // write last round target values
          for (const targetId of simulation.targetIds) {
            const target = targetStore.items[targetId];
            const uid = toUid("target", targetId);

            const valueText = `${
              Math.round(Number(lastRound.elementValues[uid].value) * 1000) / 1000
            }`;
            text += `\t${target.title.replace("\n", "")}${t(
              ":"
            )}  ${valueText}\n`;
          }
          text += "\n";

          if (resourceCount > 0) {
            text += `\t${t("objectNames.resource", 2).toUpperCase()}\n`;

            // write last round resource values
            for (const resourceId of simulation.resourceIds) {
              const resource = resources[resourceId];
              const uid = toUid("resource", resourceId);

              const valueText = `${
                Math.round(Number(lastRound.elementValues[uid].value) * 1000) / 1000
              }`;
              text += `\t${resource.title.replace("\n", "")}${t(
                ":"
              )}  ${valueText}\n`;
            }

            text += "\n";
          }

          if (indexCount > 0) {
            text += `\t${t("objectNames.index", 2).toUpperCase()}\n`;

            // write last round index values
            for (const indexId of simulation.indexIds) {
              const uid = toUid("index", indexId);
              const index = indices[indexId];

              const valueText = `${
                Math.round(Number(lastRound.elementValues[uid].value) * 1000) / 1000
              }`;
              text += `\t${index.title.replace("\n", "")}${t(
                ":"
              )}  ${valueText}\n`;
            }

            text += "\n";
          }

          // === REPORT ===

          text += t("labels.report").toUpperCase() + "\n\n";

          console.log("actionsByRound", actionsByRound);
          console.log("incidentsByRound", incidentsByRound);

          // combine actions and incidents
          const combinedActionsAndIncidents: {
            [roundIndex: string]: (IncidentItem | ActionItem)[];
          } = {};
          rounds.forEach((round, roundIndex) => {
            console.log("round", roundIndex);
            combinedActionsAndIncidents[roundIndex] = [];

            // add incidents to array
            if (incidentsByRound[roundIndex]) {
              combinedActionsAndIncidents[roundIndex] =
                combinedActionsAndIncidents[roundIndex].concat(
                  incidentsByRound[roundIndex]
                );
            }

            // add actions to array
            if (actionsByRound[roundIndex]) {
              combinedActionsAndIncidents[roundIndex] =
                combinedActionsAndIncidents[roundIndex].concat(
                  actionsByRound[roundIndex]
                );
            }
          });

          text += effectsToText(combinedActionsAndIncidents, gameContext);

          return text;
        };
      },
      getInstance(): (id: number) => Simulation {
        return (id: number): Simulation =>
          Simulation.instanceFromStore(useModelStore().simulationModelState, id);
      }
    },
    events: {
      async beforeCreate(simulation) {
        const scenario =
          simulation.scenarioId == null
            ? null
            : useScenarioStore().items[simulation.scenarioId];

        // set rounds
        if (simulation.maxRounds == null) {
          console.error("Set a maximum number of rounds for the simulation.");
          simulation.maxRounds = 10;
        }

        // add a seed for random values
        if (simulation.randomSeed == null) {
          simulation.randomSeed = Math.floor(Math.random() * 10000);
        }

        // set default simulation name
        if (simulation.title == null || simulation.title === "") {
          if (scenario == null) {
            simulation.title = "Standard";
          } else {
            simulation.title = scenario.title;
          }
          const createdAtDate = new Date(simulation.createdAt);
          simulation.title +=
            " " + formatDate(createdAtDate, "yyyy-LL-dd HH:mm");
        }

        if (scenario) {
          simulation.actionIds = [...scenario.actionIds];
          simulation.incidentIds = [...scenario.incidentIds];

          if (scenario.roundUnit) {
            simulation.roundUnit = scenario.roundUnit;
          }
          if (scenario.roundLength) {
            simulation.roundLength = scenario.roundLength;
          }
          if (scenario.timeStart) {
            simulation.timeStart = scenario.timeStart;
          }
          if (scenario.durationType) {
            simulation.durationType = scenario.durationType;
          }
        }

        // add simulation unit values
        const settings = useSettingsStore();
        if (!simulation.roundUnit) {
          simulation.roundUnit = settings.simulationRoundUnit;
        }
        if (!simulation.roundLength) {
          simulation.roundLength = settings.simulationRoundLength;
        }
        if (!simulation.timeStart) {
          simulation.timeStart = settings.simulationTimeStart;
        }
        if (!simulation.durationType) {
          simulation.durationType = settings.simulationDurationType;
        }

        const targetStore = useTargetStore();
        const resourceStore = useResourceStore();
        const indexStore = useIndexStore();

        const defaultStartElementValues = cloneDeep({
          ...targetStore.expressionValues,
          ...resourceStore.expressionValues,
          ...(simulation?.startElementValues ?? {}) as { [uid: string]: ExpressionValue }
        });

        const startIndexValues = calculateExpressionValues(indexStore.items, {
          targets: targetStore.items,
          resources: resourceStore.items,
          indices: indexStore.items,
          roundIndex: 0,
          rounds: [
            {
              elementValues: defaultStartElementValues,
            }
          ],
          simulationSettings: {
            roundUnit: simulation.roundUnit,
            roundLength: simulation.roundLength,
            roundAmount: simulation.maxRounds,
            timeStart: simulation.timeStart
          }
        });

        // combine default data with new defaults:
        simulation.startElementValues = {
          ...defaultStartElementValues,
          ...startIndexValues
        };

        return simulation;
      },
      async afterDelete(simulation, simulationStore) {
        // if simulation was the last loaded simulation, choose another as the last loaded simulation
        const simulationViewStore = useSimulationViewStore();
        if (simulationViewStore.lastLoadedGameId === simulation.id) {
          let nextGameId = null;
          const gameIds = simulationStore.ids;
          if (gameIds.length > 0) nextGameId = gameIds[0];
          simulationViewStore.set({ lastLoadedGameId: nextGameId });
          console.log("switched loaded simulationId to", nextGameId);
        }
      }
    },
    actions: {
      /**
       * @deprecated Use deleteElementValue instead.
       */
      deleteTargetValue({ id, targetId }: { id: number; targetId: number }) {
        delete this.items[id].startElementValues[`target_${targetId}`];
      },
      /**
       * @deprecated Use deleteElementValue instead.
       */
      deleteResourceValue({
        id,
        resourceId
      }: {
        id: number;
        resourceId: number;
      }) {
        delete this.items[id].startElementValues[`resource_${resourceId}`];
      },
      /**
       * @deprecated Use deleteElementValue instead.
       */
      deleteIndexValue({ id, indexId }: { id: number; indexId: number }) {
        delete this.items[id].startElementValues[`index_${indexId}`];
      },

      deleteElementValue({
        id,
        uid
      }: {
        id: number;
        uid: string;
      }) {
        delete this.items[id].startElementValues[uid];
      },

      /**
       *
       */
      updateRound(id: number, roundOptions: { index: number }): Simulation {
        const simulation: Simulation = this.getInstance(id);
        const { index } = roundOptions;
        Object.assign(simulation.rounds[index], roundOptions);
        this.saveSimulationState(simulation);
        return simulation;
      },

      /**
       *
       */
      updateRoundAmount({ id, maxRounds }: { id: number; maxRounds: number }): Simulation {
        const simulation = this.getInstance(id);
        simulation.setMaxRounds(maxRounds);
        this.saveSimulationState(simulation);
        return simulation;
      },

      /**
       *
       */
      saveSimulationState(simulationInstance: SimulationItem): void {
        this.$patch((state) => {
          const storeItem = state.items[simulationInstance.id];
          if (storeItem == null) {
            console.error(`no simulation in store w/ id ${simulationInstance.id}`);
            return;
          }

          const dataToAssign: Partial<SimulationItem> = { ...simulationInstance };
          
          // ensure that we're not saving anything to the 
          // store that is not in the store item.
          for (const prop in dataToAssign) {
            if (!(prop in storeItem)) {
              delete dataToAssign[prop as keyof SimulationItem];
            }
          }

          assign(storeItem, cloneDeep(dataToAssign));
        });
      },

      async initNewSimulation(simulationData: Partial<SimulationItem>): Promise<SimulationItem> {
        const targetStore = useTargetStore();
        const resourceStore = useResourceStore();
        const indexStore = useIndexStore();

        const defaultSimulationData: Partial<SimulationItem> = {
          startElementValues: cloneDeep({
            ...targetStore.expressionValues,
            ...resourceStore.expressionValues,
            ...indexStore.expressionValues
          }),
  
          targetIds: targetStore.ids.slice(),
          resourceIds: resourceStore.ids.slice(),
          indexIds: indexStore.ids.slice(),
        };

        return this.create({
          ...defaultSimulationData,
          ...simulationData
        });
      },

      /**
       * Export simulation to file.
       */
      async export(simulation: Partial<SimulationItem>): Promise<void> {
        const simulationToExport = {
          ...simulation
        };
        delete simulationToExport.id;
        const asJSON = JSON.stringify(simulationToExport);
        const blob = new Blob([asJSON], { type: "application/json" });
        downloadBlob(simulation.title || "", blob);
      },

      /**
       * Import simulation data as a new simulation
       */
      async import(simulationData: Partial<SimulationItem>): Promise<SimulationItem> {
        const { t } = i18n;
        const newItem: SimulationItem = await this.create({
          ...simulationData,
          title: simulationData.title + t("labels.importSuffix")
        });

        return this.items[newItem.id];
      },

      /**
       *
       */
      async duplicate(simulationId: number): Promise<SimulationItem> {
        const { t } = i18n;
        const simulation = this.items[simulationId];

        const newItem = await this.create({
          ...simulation,
          title: simulation.title + t("labels.copySuffix")
        });

        return this.items[newItem.id];
      },

      /**
       * Create a new simulation with the same settings
       * and actionplans (actions/impulses) as an
       * existing simulation.
       */
      async duplicateAndReset(simulationId: number): Promise<SimulationItem> {
        const { t } = i18n;
        const simulation = this.items[simulationId];

        const simulationData: Partial<SimulationItem> = {
          ...simulation,
          title: simulation.title + t("labels.copySuffix"),
          complete: false,
          currentRoundIndex: -1,
          endReason: null,
          rounds: []
        };
        delete simulationData.startElementValues;
        delete simulationData.indexIds;
        delete simulationData.resourceIds;
        delete simulationData.targetIds;

        const newItem = await this.initNewSimulation(simulationData);

        return this.items[newItem.id];
      },

      /**
       *
       */
      addScheduledEffect({
        simulationId,
        roundIndex,
        affectedId,
        affectedType,
        impulseExpressions = [""]
      }: {
        simulationId: number;
        roundIndex: number;
        affectedId: number;
        affectedType: "target" | "resource";
        impulseExpressions: string[];
      }) {
        const simulation = this.items[simulationId];

        const id = uuidv4();
        const createdImpulse: ScheduledEffectItem = {
          id,
          uid: `sef-${id}`,
          activatorId: id,
          activatorType: "scheduledEffect" as const,
          affectedType,
          affectedId,
          impulseExpressions,
          type: "scheduledEffect" as const,
          createdAt: Date.now(),

          hint: "",
          visibility: "visible",
          pendingEffectsVisible: true,
          pendingEffectsOpenable: true
        };

        const roundActionPlan = this.getActionPlan(simulationId, roundIndex);
        roundActionPlan.scheduledEffects.push(createdImpulse);
        simulation.actionPlans[roundIndex] = roundActionPlan;

        return createdImpulse;
      },

      removeScheduledEffect({
        simulationId,
        roundIndex,
        scheduledEffectId
      }: {
        simulationId: number;
        roundIndex: number;
        scheduledEffectId: string;
      }) {
        const simulation = this.items[simulationId];
        const roundActionPlan = this.getActionPlan(simulationId, roundIndex);

        const index = roundActionPlan.scheduledEffects.findIndex(
          (impulse) => impulse.id === scheduledEffectId
        );
        if (index > -1) {
          roundActionPlan.scheduledEffects.splice(index, 1);
        }
        simulation.actionPlans[roundIndex] = roundActionPlan;
      },

      /**
       *
       */
      updateScheduledEffect({
        simulationId,
        roundIndex,
        effect
      }: {
        simulationId: number;
        roundIndex: number;
        effect: { id: string; impulseExpressions: string[] };
      }) {
        const simulation = this.items[simulationId];
        const roundActionPlan = this.getActionPlan(simulationId, roundIndex);

        // find the effect to update
        const effectIndex = roundActionPlan.scheduledEffects.findIndex(
          (e) => e.id == effect.id
        );

        // replace the impulseExpressions
        const original = roundActionPlan.scheduledEffects[effectIndex];
        roundActionPlan.scheduledEffects[effectIndex] = {
          ...original,
          impulseExpressions: effect.impulseExpressions
        };
        simulation.actionPlans[roundIndex] = roundActionPlan;
      },

      updateActionIds({
        simulationId,
        roundIndex,
        actionIds
      }: {
        simulationId: number;
        roundIndex: number;
        actionIds: number[];
      }) {
        const simulation = this.items[simulationId];
        const roundActionPlan = this.getActionPlan(simulationId, roundIndex);

        roundActionPlan.plannedActionIds = actionIds;
        simulation.actionPlans[roundIndex] = roundActionPlan;
      },

      addAction({
        simulationId,
        roundIndex,
        actionId,
        actionIndex
      }: {
        simulationId: number;
        roundIndex: number;
        actionId: number;
        actionIndex?: number;
      }) {
        const simulation = this.items[simulationId];
        const roundActionPlan = this.getActionPlan(simulationId, roundIndex);
        if (roundActionPlan.plannedActionIds.includes(actionId)) {
          return false;
        }

        // add the action to a specific index if required
        if (actionIndex != null) {
          roundActionPlan.plannedActionIds.splice(actionIndex, 0, actionId);
        } else {
          roundActionPlan.plannedActionIds.push(actionId);
        }

        simulation.actionPlans[roundIndex] = roundActionPlan;
        return true;
      },

      removeAction({
        simulationId,
        roundIndex,
        actionId
      }: {
        simulationId: number;
        roundIndex: number;
        actionId: number;
      }) {
        const simulation = this.items[simulationId];
        const roundActionPlan = this.getActionPlan(simulationId, roundIndex);

        const actionIds = [...roundActionPlan.plannedActionIds];
        actionIds.splice(actionIds.indexOf(actionId), 1);
        roundActionPlan.plannedActionIds = actionIds;
        simulation.actionPlans[roundIndex] = roundActionPlan;
      },
      moveActions({
        simulationId,
        actionIds,
        roundIndexSource,
        roundIndexTarget,
        targetActionIndex
      }: {
        simulationId: number;
        actionIds: number[];
        roundIndexSource: number;
        roundIndexTarget: number;
        targetActionIndex: number;
      }): void {
        for (const actionId of actionIds) {
          if (
            this.addAction({
              simulationId,
              roundIndex: roundIndexTarget,
              actionId,
              actionIndex: targetActionIndex
            })
          ) {
            this.removeAction({
              simulationId,
              roundIndex: roundIndexSource,
              actionId
            });
          }
        }
      },

      updateRoundDocs({
        simulationId,
        roundIndex,
        docs
      }: {
        simulationId: number;
        roundIndex: number;
        docs: Record<string, string>;
      }) {
        const simulation = this.items[simulationId];
        const roundActionPlan = this.getActionPlan(simulationId, roundIndex);
        roundActionPlan.docs = docs;
        simulation.actionPlans[roundIndex] = roundActionPlan;
      }
    }
  }
);

function effectsToText(
  itemsByRound: null | { [roundIndex: number]: (ActionItem | IncidentItem)[] },
  {
    simulationId,
    rounds
  }: { simulationId: number; rounds: SimulationRoundItem[] }
) {
  const { t } = i18n;

  // no rounds
  if (itemsByRound == null || Object.values(itemsByRound).length == 0) {
    return t("texts.noRoundsPlayed");
  }

  let text = "";

  const simulation = useSimulationStore().items[simulationId];

  const targetStore = useTargetStore();
  const resourceStore = useResourceStore();
  const modelStore = useModelStore();
  const effectStore = useEffectStore();

  const elements = {
    ...resourceStore.byUid,
    ...targetStore.byUid
  }

  const currentRoundIndex = simulation.currentRoundIndex ?? -1;

  const lastCompletedRoundIndex = simulation.complete
    ? currentRoundIndex
    : currentRoundIndex - 1;

  for (const roundIndex in itemsByRound) {
    const round = rounds[roundIndex];
    const roundActionPlan = useSimulationStore().getActionPlan(
      simulationId,
      roundIndex
    );
    const roundTitle = formatRoundName(
      simulation.durationType,
      simulation.roundUnit,
      simulation.roundLength,
      parseInt(roundIndex) * 1,
      simulation.timeStart
    );

    const roundActions = itemsByRound[roundIndex];

    // round is completed:
    text += `${roundTitle}\n`;

    if (roundActionPlan?.docs?.notes) {
      const notesHTMLContent = document.createElement("div");
      notesHTMLContent.innerHTML = roundActionPlan?.docs?.notes;
      const htmlAsString = notesHTMLContent.innerText;
      text += `\t${htmlAsString} \n`;
    }

    // round not completed yet:
    if (round.index > lastCompletedRoundIndex) {
      text += `\t${t("texts.XNotPlayedYet", [""])} \n`;
      continue;
    } else {
      text += "\n";
    }

    if (roundActions == null || roundActions.length == 0) {
      text += "\t" + t("texts.noActionsOrIncidentsPlayed") + "\n\n";
      continue;
    }

    for (const actionItem of roundActions) {
      const typeName = t(`objectNames.${actionItem.type}`);
      text += `\t${typeName}${t(":")} ${actionItem.title.replace("\n", "")}\n`;

      let wasPrevented = false;
      // preventing conditions if action:
      if (actionItem.type === "action") {
        if (round.preventedActions && round.preventedActions[actionItem.id]) {
          wasPrevented = true;
          text += `\t${t("texts.actionPrevented")}\n`;

          // TODO (long-term): include the reasons why the action was prevented
        }

        // not payable
        if (
          round.notPayableActions &&
          round.notPayableActions[actionItem.id]
        ) {
          wasPrevented = true;
          const preventedAction = round.notPayableActions[actionItem.id];
          const element = elements[preventedAction.elementUid];

          const preventedText = t("texts.notEnough$resource", {
            resource: element?.title ?? t('labels.unknown'),
            available: Math.round(preventedAction.availableValue * 10) / 10,
            needed: preventedAction.neededValue
          });

          text += `\t${preventedText}\n`;
        }
      }

      // show effects created by that actionItem
      if (!wasPrevented) {
        const roundImpulses = round.impulses;

        // costs
        if (actionItem.type == "action") {
          const costs = actionItem.costs || {};

          costs.forEach((cost) => {
            const { type, id } = splitUid(cost.elementUid);

            const impulse: ImpulseItem = get(roundImpulses, [type, id]);
            const element = elements[cost.elementUid];

            if (impulse == null) {
              console.warn("impulse not found", roundImpulses, cost);
              return;
            }

            const impulsePart = impulse.sourceValues.find((sourceValue) => {
              return (
                sourceValue.type == actionItem.type &&
                sourceValue.id == actionItem.id &&
                sourceValue.effectType == "cost"
              );
            });

            if (impulsePart) {
              const costText = element.title.replace("\n", "") + ` [${formatNumber(impulsePart.value, "points")}]`;

              text += `\t${costText}\n`;
            } else {
              console.warn("impulsePart not found", impulse, element, cost);
            }
          });
        }

        const effects = effectStore.getByActivator(actionItem) || [];
        effects.forEach((effect) => {
          if (effect.affectedId == null) return;

          const impulse: ImpulseItem = get(roundImpulses, [
            effect.affectedType,
            effect.affectedId
          ]);

          const affectedItem = modelStore.getItem(
            effect.affectedType,
            effect.affectedId
          ) as TargetItem | ResourceItem;

          const impulsePart = impulse.sourceValues.find((sourceValue) => {
            return (
              sourceValue.type == actionItem.type &&
              sourceValue.id == actionItem.id &&
              sourceValue.effectType == "effect"
            );
          });

          if (impulsePart) {
            const affectedUid = toUid(effect.affectedType, effect.affectedId);
            const effectText =
              affectedItem.title.replace("\n", "") +
              ` [${formatNumber(impulsePart.value, "points")}] → ${
                formatNumber(
                  Number(round.elementValues[affectedUid].value)
                )
              }`;

            text += `\t${effectText}\n`;
          } else {
            console.warn(
              "impulsePart not found",
              impulse,
              affectedItem,
              effect
            );
          }
        });
      }

      text += "\n";
    }

    // TODO:
    // This has to be formatted...
    // the impulse expression appear as plain text currently.
    if (roundActionPlan.scheduledEffects.length > 0) {
      text += `\t${t("objectNames.scheduledEffect", 2)}\n`.toUpperCase();
      for (const impulse of roundActionPlan.scheduledEffects) {
        const impulseAmountText = impulse.impulseExpressions.join();
        let title = "";
        if (impulse.affectedType === "target") {
          title =
            targetStore.items?.[impulse.affectedId]?.title ??
            t("labels.unnamed");
        }
        if (impulse.affectedType === "resource") {
          title =
            resourceStore.items?.[impulse.affectedId]?.title ??
            t("labels.unnamed");
        }
        text += `\t\t${title} ${impulseAmountText}\n`;
      }
      text += "\n";
    }
  }
  return text;
}

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHotItemStore(useSimulationStore, import.meta.hot)
  );
}
